import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import UserAccountModal from "../common/UserAccountModal";
import SupportModal from "./SupportModal";
import Button from "../common/Button";
import useUserEmail from "../../utils/useUserEmail";
import useLinkedInAccounts from "../../utils/useLinkedInAccounts";
import { getBrandProfile } from "../../utils/brandColors";

import logo from "../../assets/sideBar/logo-feelim.svg";
import hamburger from "../../assets/sideBar/menu.svg";
import close from "../../assets/sideBar/close-icon.svg";
import create from "../../assets/sideBar/create-post.svg";
import scheduled from "../../assets/sideBar/scheduled.svg";
import trending from "../../assets/sideBar/trending.svg";
import user from "../../assets/sideBar/user.svg";
import logout from "../../assets/sideBar/logout.svg";
import support from "../../assets/sideBar/support.svg";
import circle from "../../assets/navbar/green-circle.svg";
import redCircle from "../../assets/navbar/red-circle.svg";
import plug from "../../assets/navbar/connect-plug.svg";
import disconnectPlug from "../../assets/navbar/disconnect-plug.svg";
import { ChevronDown } from "lucide-react";

const SideBar = ({ isSidebarOpen, setIsSidebarOpen, hasContent = false, onBeforeConnect }) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);

  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showNewPostDialog, setShowNewPostDialog] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [userName, setUserName] = useState(() => {
    const profile = getBrandProfile();
    return [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
  });
  const [open, setOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const email = useUserEmail();
  const { isConnected: isLinkedInConnected, status } = useLinkedInAccounts(email);

  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const handleNewPost = () => {
    setShowNewPostDialog(true);
  };

  /* ============================================================
     COMPONENT RETURN
  ============================================================ */
  return (
    <>
      {/* ===== DESKTOP SIDEBAR ===== */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black-900 bg-opacity-50 z-30 transition-opacity duration-300 ease-in-out"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`hidden md:block fixed left-0 top-0 h-screen bg-white py-4 shadow-lg z-40 transition-all duration-300 ease-in-out
    ${isSidebarOpen ? "w-[374px]" : "w-[125px]"}
  `}
      >
        <div className="flex flex-col">
          <div
            className={`flex items-center ${isSidebarOpen ? "justify-between px-4" : "justify-center"
              } w-full mb-4`}
          >
            {isSidebarOpen && <img src={logo} alt="logo" />}
            <button
              className="p-2 rounded-lg"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <img src={hamburger} alt="hamburger" />
            </button>
          </div>

          {/* Navigation */}
          <nav
            className={`flex flex-col flex-grow text-black-600 ${isSidebarOpen ? "items-start px-4" : "items-center"
              } space-y-3 pt-6 w-full`}
          >
            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              onClick={() => {
                if (hasContent) {
                  setShowNewPostDialog(true);
                } else {
                  navigate("/create", { state: { reset: true, ts: Date.now() } });
                }
              }}
              className={isSidebarOpen ? "w-full" : "w-[56px]"}
            >
              <img src={create} alt="create" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg">
                  {t("sidebar.create")}
                </span>
              )}
            </Button>
            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              onClick={() => {
                if (hasContent) {
                  setShowLeaveDialog(true);
                } else {
                  navigate("/draft");
                }
              }}
              className={isSidebarOpen ? "w-full" : "w-[56px]"}
            >
              <img src={scheduled} alt="scheduled" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg">
                  {t("draft.scheduledPost")}
                </span>
              )}
            </Button>
            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              className={`${isSidebarOpen ? "w-full" : "w-[56px]"} opacity-50 cursor-not-allowed relative`}
              
              onMouseEnter={() => !isTouchDevice && setOpen1(true)}
              onMouseLeave={() => !isTouchDevice && setOpen1(false)}
            >
              <img src={trending} alt="trending" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg font-medium">{t("sidebar.trend")}</span>
              )}
              {open1 && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-[140px] bg-gray-800 text-white text-sm rounded-lg shadow-xl p-2 z-50">
                  <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-800 rotate-45" />
                  <p>{t("updates.newFeature")}</p>
                </div>
              )}
            </Button>
          
            <span className="text-lg text-black-800 font-bold mt-4">
              Account
            </span>

            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              onClick={() => setIsAccountModalOpen(true)}
              className={isSidebarOpen ? "w-full" : "w-[56px]"}
            >

              <img src={user} alt="" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg">
                  {userName || t("sidebar.account")}
                </span>
              )}
            </Button>
            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              onClick={() => setIsSupportModalOpen(true)}
              className={isSidebarOpen ? "w-full" : "w-[56px]"}
            >
              <img src={support} alt="support" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg">
                  {t("sidebar.support")}
                </span>
              )}
            </Button>
            <Button
              variant={isSidebarOpen ? "sidebarExpanded" : "sidebarIcon"}
              className={isSidebarOpen ? "w-full h-[46px]" : "w-[56px] md:h-[42px] h-[58px]"}
              onClick={handleLogout}
            >
              <img src={logout} alt="" />
              {isSidebarOpen && (
                <span className="ml-3 text-lg">
                  {t("sidebar.logout")}
                </span>
              )}
            </Button>
          </nav>
        </div>
      </aside>

      {/* ===== MOBILE FULLSCREEN MENU ===== */}
      {/* Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black-900/50 z-40 transition-opacity duration-300
        ${isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Drawer Panel */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 w-full max-w-[420px] bg-white z-50 
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 shrink-0">
          <img src={logo} alt="logo" className="h-8" />
          <button onClick={() => setIsSidebarOpen(false)}>
            <img src={close} alt="close" />
          </button>
        </div>

        {/* Menu Items */}
        <nav
          className={`flex flex-col flex-grow min-h-0 overflow-y-auto overscroll-contain text-black-600 ${isSidebarOpen ? "items-start px-4" : "items-center"
            } space-y-3 pt-6 pb-6 w-full`}
        >
          {/* LinkedIn status */}
          {status !== "loading" && (
            <Button
              variant="social"
              onClick={() => {
                setIsSidebarOpen(false);
                setIsAccountModalOpen(true);
              }}
              className="w-full"
            >
              <img src={isLinkedInConnected ? circle : redCircle} alt="status" />
              <span>
                {isLinkedInConnected ? t("buttons.connected") : t("buttons.notConnected")}
              </span>
              <img src={isLinkedInConnected ? plug : disconnectPlug} alt="plug" className="ml-auto" />
            </Button>
          )}
          <Button
            variant="sidebarExpanded"
            onClick={() => {
              setIsSidebarOpen(false);
              if (hasContent) {
                setShowNewPostDialog(true);
              } else {
                navigate("/create", { state: { reset: true, ts: Date.now() } });
              }
            }}
            className="w-full py-3 mt-5"
          >
            <img src={create} className="mr-3" alt="" />
            <span>{t("sidebar.create")}</span>
          </Button>
          <Button
            variant="sidebarExpanded"
            onClick={() => {
              setIsSidebarOpen(false);
              if (hasContent) {
                setShowLeaveDialog(true);
              } else {
                navigate("/draft");
              }
            }}
            className="w-full py-3"
          >
            <img src={scheduled} className="mr-3" alt="" />
            <span>{t("draft.scheduledPost")}</span>
          </Button>
          <Button
            variant="sidebarExpanded"
            className="w-full opacity-50 cursor-not-allowed py-3"
            disabled
          >
            <img src={trending} className="mr-3" alt="" />
            <span className="text-lg font-medium">{t("sidebar.trend")}</span>
          </Button>

          {/* Account */}
          <span className="text-lg text-black-800 font-bold mt-4">
            Account
          </span>

          <Button
            variant="sidebarExpanded"
            onClick={() => {
              setIsSidebarOpen(false);
              setIsAccountModalOpen(true);
            }}
            className="w-full py-3"
          >
            <img src={user} className="mr-3" alt="" />
            <span>{userName || t("sidebar.account")}</span>
          </Button>
          <Button
            variant="sidebarExpanded"
            onClick={() => setIsSupportModalOpen(true)}
            className="w-full py-3"
          >
            <img src={support} className="mr-3" alt="support" />
            <span>{t("sidebar.support")}</span>
          </Button>
          <Button
            variant="sidebarExpanded"
            onClick={handleLogout}
            className="w-full py-3"
          >
            <img src={logout} className="mr-3" alt="" />
            <span>{t("sidebar.logout")}</span>
          </Button>
        </nav>
      </div>

      <UserAccountModal
        userName={userName}
        setUserName={setUserName}
        userEmail={email}
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        onBeforeConnect={onBeforeConnect}
      />

      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />

      {showLeaveDialog && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 w-full max-w-[400px] shadow-xl">
            <h3 className="text-lg font-bold mb-4">
              {t("dialog.leavePost.title")}
            </h3>
            <p className="bg-link-100 text-black-800 text-lg px-2 mb-4">
              {t("dialog.leavePost.message")}
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3 text-sm sm:text-lg font-bold">
              <Button
                variant="ghost"
                onClick={() => setShowLeaveDialog(false)}
                className="w-full sm:w-auto"
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                variant="dark"
                onClick={() => {
                  setShowLeaveDialog(false);
                  navigate("/draft");
                }}
                className="w-full sm:w-auto"
              >
                {t("buttons.continue")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {showNewPostDialog && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 w-full max-w-[400px] shadow-xl">
            <h3 className="text-lg font-bold mb-4">
              {t("dialog.newPost.title")}
            </h3>
            <p className="bg-link-100 text-black-800 text-lg px-2 mb-4">
              {t("dialog.newPost.message")}
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3 text-sm sm:text-lg font-bold">
              <Button
                variant="ghost"
                onClick={() => setShowNewPostDialog(false)}
                className="w-full sm:w-auto"
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                variant="dark"
                onClick={() => {
                  setShowNewPostDialog(false);
                  navigate("/create", { state: { reset: true, ts: Date.now() } });
                }}
                className="w-full sm:w-auto"
              >
                {t("buttons.continue")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideBar;